import { ColorHoverVariants, colorHoverVariants, isExternalUrl } from '@synergy/utils';
import { ElementType, PropsWithChildren, ReactElement } from 'react';
import { Link, LinkProps, SxProps, Theme, styled, useTheme } from '@mui/material';
import { Link as RouterLink, To } from 'react-router-dom';

interface StyledAnchorProps {
    textColor?: string;
    hoverColor?: string;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const GenericStyledAnchor = <T extends ElementType>() =>
    styled(Link<T>, {
        shouldForwardProp: prop => prop !== 'textColor' && prop !== 'hoverColor',
    })<StyledAnchorProps>(({ textColor, hoverColor }) => ({
        color: textColor,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',

        '&:hover': {
            color: hoverColor,
        },
    }));

const StyledAnchor = GenericStyledAnchor();

export interface PoolLinkProps extends Omit<PropsWithChildren<LinkProps>, 'href'> {
    colorVariant?: ColorHoverVariants;
    textColor?: string;
    hoverColor?: string;
    sx?: SxProps<Theme>;
    href?: To;
    as?: ElementType;
    /** Router state handed to the target page; ignored for external links. */
    state?: unknown;
}

export const PoolLink = (props: PoolLinkProps): ReactElement => {
    const { colorVariant, textColor, hoverColor, href = '', as, state, ...linkProps } = props;
    const theme = useTheme();

    const isExternal = isExternalUrl(href);

    const commonProps: Omit<PoolLinkProps, 'href'> = {
        variant: 'inherit',
        underline: 'none',
        textColor: textColor ?? colorHoverVariants(theme)[colorVariant || 'black'].textColor,
        hoverColor: hoverColor ?? colorHoverVariants(theme)[colorVariant || 'black'].hoverColor,
        ...linkProps,
    };

    return isExternal ? (
        <StyledAnchor component="a" href={typeof href === 'string' ? href : href?.pathname} {...commonProps} />
    ) : (
        <StyledAnchor component={RouterLink} to={href} state={state} as={as} {...commonProps} />
    );
};
